/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { upperCase } from "lodash";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { DeleteModalComponent } from "../../../shared/components/delete-modal/delete-modal.component";
import { QuickViewModalComponent } from "../../../shared/components/quick-view-modal/quick-view-modal.component";
import { fileIcons } from "../../../shared/constants/icons";
import { UserInfo } from "../../../shared/types/user-info";
import { getAssetAttachmentSearchFilterOptions } from "../assets/asset";
import { Asset, AssetAttachment } from "./asset.type";
import { AssetService } from "./asset.service";

@Injectable({
  providedIn: "root",
})
export class AssetAttachmentTableService {
  currentUser: UserInfo;

  constructor(
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private assetService: AssetService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  getAssetAttachmentsTableConfig = (config?: DataTableConfig): DataTable<AssetAttachment> => {
    const { hidden, shown, actionColumn, inlineEdit } = config || {};

    const columns: DataTableColumn<AssetAttachment>[] = (
      [
        {
          prop: "extension",
          name: "File Type",
          icon: {
            class: this.getIconClass,
            tooltip: this.getTooltipValue,
          },
        },
        {
          prop: "name",
          name: "Name",
          link: {
            target: "_blank",
            path: (assetAttachment) => assetAttachment.url,
            useHref: true,
          },
        },
        {
          prop: "description",
          name: "Description",
          inlineEdit: {
            control: "input",
            disabled: (assetAttachment) =>
              inlineEdit?.disabled?.(assetAttachment) ||
              this.currentUser.hasRight(["viewer"]) ||
              this.currentUser.isCompanyOrViewAsCompany(),
          },
        },
      ] as DataTableColumn<AssetAttachment>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (assetAttachment) =>
            col?.inlineEdit?.disabled?.(assetAttachment) || !allowUpdateEntity(assetAttachment, this.currentUser),
        },
      }));

    return {
      id: "assetAttachment",
      entityName: "Attachment",
      syncWithQueryParams: true,
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "zoom_in",
              text: "Quick View",
              function: this.viewAssetAttachment,
            },
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteAssetAttachment,
              hidden: (assetAttachment) => !allowDeleteEntity(assetAttachment, this.currentUser),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getAssetAttachmentSearchFilterOptions(this.currentUser, { hidden, shown }),
        },
      ],
    };
  };

  getAssetAttachments = (event: GetDataEvent<AssetAttachment>, asset: Asset): void => {
    const { onError, onResult, queryParams } = event;
    this.assetService
      .getAssetAttachments(asset.id, {
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  update = (event: UpdateDataEvent<AssetAttachment>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      const data = { description: updatedValues.description };
      this.assetService.updateAssetAttachment(row.assetId, row.id, data).subscribe(
        (result): void => {
          onResult(result);
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  // #region Private methods
  private viewAssetAttachment = (assetAttachment: AssetAttachment): void => {
    const data = [
      { name: "Name", value: assetAttachment.name },
      { name: "Description", value: assetAttachment.description },
    ];
    this.dialog.open(QuickViewModalComponent, {
      width: "500px",
      data: {
        title: "Quick-view",
        data: data,
      },
      panelClass: "quick-view-modal",
    });
  };

  private deleteAssetAttachment = (
    assetAttachment: AssetAttachment,
    table: DataTableComponent<AssetAttachment>,
  ): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "assetAttachment",
        data: assetAttachment,
        url: `${this.assetService.basePath}/${assetAttachment.assetId}/asset-attachments`,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private getTooltipValue = (assetAttachment: AssetAttachment): string => upperCase(assetAttachment.extension);

  private getIconClass = (assetAttachment: AssetAttachment): string =>
    `fa ${fileIcons[assetAttachment.extension] || fileIcons.default}`;

  // #endregion
}
